import { loadSettings } from "../ai/localMemory";

/**
 * Minimal synthesized sound effects via the Web Audio API — no asset files required, so
 * the game gets audio feedback today with zero licensing concerns. Each SFX is a short
 * oscillator + gain envelope. Music is intentionally left for a future pass with real
 * licensed audio.
 *
 * Browser autoplay policy: an AudioContext starts "suspended" until a user gesture, so we
 * lazily create + resume it on the first playSfx call (which always follows a tap/click).
 */
export type SfxName = "click" | "build" | "summon" | "ability" | "win" | "lose" | "levelUp" | "deny";

type ToneStep = { freq: number; type?: OscillatorType; start: number; duration: number; gain?: number };

// Each effect is a tiny score of tones (frequencies in Hz, times in seconds).
const SFX: Record<SfxName, ToneStep[]> = {
  click: [{ freq: 440, type: "triangle", start: 0, duration: 0.06, gain: 0.18 }],
  build: [
    { freq: 320, type: "square", start: 0, duration: 0.07, gain: 0.16 },
    { freq: 480, type: "square", start: 0.06, duration: 0.08, gain: 0.16 }
  ],
  summon: [
    { freq: 392, type: "sawtooth", start: 0, duration: 0.08, gain: 0.14 },
    { freq: 587, type: "sawtooth", start: 0.07, duration: 0.1, gain: 0.14 }
  ],
  ability: [
    { freq: 660, type: "triangle", start: 0, duration: 0.08, gain: 0.16 },
    { freq: 990, type: "triangle", start: 0.07, duration: 0.12, gain: 0.16 }
  ],
  win: [
    { freq: 523, type: "triangle", start: 0, duration: 0.12, gain: 0.2 },
    { freq: 659, type: "triangle", start: 0.12, duration: 0.12, gain: 0.2 },
    { freq: 784, type: "triangle", start: 0.24, duration: 0.18, gain: 0.2 },
    { freq: 1047, type: "triangle", start: 0.42, duration: 0.26, gain: 0.2 }
  ],
  lose: [
    { freq: 392, type: "sawtooth", start: 0, duration: 0.16, gain: 0.18 },
    { freq: 311, type: "sawtooth", start: 0.16, duration: 0.18, gain: 0.18 },
    { freq: 233, type: "sawtooth", start: 0.34, duration: 0.3, gain: 0.18 }
  ],
  levelUp: [
    { freq: 659, type: "square", start: 0, duration: 0.09, gain: 0.16 },
    { freq: 880, type: "square", start: 0.09, duration: 0.09, gain: 0.16 },
    { freq: 1175, type: "square", start: 0.18, duration: 0.2, gain: 0.16 }
  ],
  deny: [{ freq: 196, type: "square", start: 0, duration: 0.16, gain: 0.16 }]
};

class AudioManagerImpl {
  private ctx?: AudioContext;
  private muted = false;
  private initialized = false;

  /** Read the persisted mute setting. Call once a scene is up (settings exist by then). */
  syncFromSettings() {
    try {
      this.muted = Boolean(loadSettings().muted);
    } catch {
      this.muted = false;
    }
    this.initialized = true;
  }

  setMuted(muted: boolean) {
    this.muted = muted;
  }

  isMuted() {
    return this.muted;
  }

  playSfx(name: SfxName) {
    if (!this.initialized) this.syncFromSettings();
    if (this.muted) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    if (ctx.state === "suspended") {
      void ctx.resume().catch(() => undefined);
    }
    const steps = SFX[name];
    const now = ctx.currentTime;
    for (const step of steps) {
      this.playTone(ctx, step, now);
    }
  }

  private playTone(ctx: AudioContext, step: ToneStep, baseTime: number) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = step.type ?? "triangle";
    osc.frequency.value = step.freq;
    const startAt = baseTime + step.start;
    const peak = step.gain ?? 0.15;
    // Quick attack + exponential-ish decay so tones don't click.
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.linearRampToValueAtTime(peak, startAt + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + step.duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + step.duration + 0.02);
  }

  private ensureContext(): AudioContext | undefined {
    if (this.ctx) return this.ctx;
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return undefined;
    try {
      this.ctx = new Ctor();
    } catch {
      this.ctx = undefined;
    }
    return this.ctx;
  }
}

/** Shared singleton so any scene can fire SFX without threading an instance through. */
export const AudioManager = new AudioManagerImpl();
